import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { roomDataFormatter } from "./SearchRedux";
import { searchQuery } from "./searchApi";
import MenuDialog from "../Shared/MenuDialog";
import AutocompleteMenu from "../Shared/AutocompleteMenu";

async function queryRoom(queryStr, idToken) {
  const queries = queryStr.split(" ");
  if (queries.length > 0) {
    // Room numbers don't have spaces in them BUT Clinical Room Numbers can
    // Add the unsplit query so the user can search for them.
    queries.unshift(queryStr);
  }

  const responseArrays = await Promise.all(
    queries.map(async (q) => {
      if (!q || q.length < 2) return [];
      // Must encode the query since it might have spaces
      // or slashes in it.
      const response = await searchQuery(
        "/supersearch/" + encodeURIComponent(q),
        idToken,
      );
      const isValidResponse =
        !response.error &&
        response.data &&
        ((response.data.rmnbr && response.data.rmnbr.length) ||
          (response.data.rmrecnbr && response.data.rmrecnbr.length));
      if (!isValidResponse) {
        return [];
      }
      const rmnbrs = response.data.rmnbr ? response.data.rmnbr : [];
      const rmrecnbrs = response.data.rmrecnbr ? response.data.rmrecnbr : [];
      const rooms = [...rmnbrs, ...rmrecnbrs];
      return roomDataFormatter(rooms);
    }),
  );

  const allResults = responseArrays.flat();
  const sortedResults = [
    ...allResults.filter((item) => item.isexact),
    ...allResults.filter((item) => !item.isexact),
  ];
  const rmrecnbrsSet = new Set();
  const prunedResults = [];

  for (const result of sortedResults) {
    if (!rmrecnbrsSet.has(result.rmrecnbr)) {
      prunedResults.push(result);
      rmrecnbrsSet.add(result.rmrecnbr);
    }
  }

  return prunedResults;
}

function RoomAutocomplete({ open, selectHandler }) {
  const optionDisplay = {
    selected: ["rmnbr", "clinrmnbr", "rmrecnbr", "bld_descrshort"],
    label: "rmnbr",
    label2: "clinrmnbr",
    label2Tooltip: "Clinical Room Number",
    labelId: "rmrecnbr",
    labelContext: "bld_descrshort",
  };
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);
  const defaultData = useSelector(
    (state) => state.searchReducer.defaultData.rooms,
  );
  const idToken = useSelector((state) => state.authReducer.idToken);
  useEffect(() => {
    setOptions(defaultData);
  }, [defaultData]);

  const latestRequestId = useRef(0);
  const onInputChange = async (input) => {
    const currentRequestId = ++latestRequestId.current;

    if (input.length < 2) {
      setOptions(defaultData || []);
      setLoading(false);
    } else {
      setLoading(true);
      const rooms = await queryRoom(input, idToken);

      // Handle out-of-order responses by only honoring the
      // response from the "most current" request.
      if (currentRequestId === latestRequestId.current) {
        setLoading(false);
        setOptions(rooms);
      } else {
        console.log("Skipping out-of-order response for:", input);
      }
    }
  };

  return (
    <AutocompleteMenu
      open={open}
      loading={loading}
      selectHandler={selectHandler}
      inputLabel="Room"
      optionDisplay={optionDisplay}
      alwaysShowContext={true}
      options={options}
      onInputChange={onInputChange}
    />
  );
}

export default function RoomMenu({
  visible,
  selectHandler,
  onCloseHandler,
  offset,
}) {
  return (
    <MenuDialog
      offset={offset}
      visible={visible}
      selectHandler={selectHandler}
      onCloseHandler={onCloseHandler}
      menuType="room"
    >
      <RoomAutocomplete />
    </MenuDialog>
  );
}
